import React from 'react';
import { usePermissions } from '../../shared/contexts/permissions.context';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean; // If true, user must have ALL permissions. If false, user needs ANY permission
  fallback?: React.ReactNode;
  showError?: boolean;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  showError = false,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="text-neutral-secondary-mid">Permissions laden...</div>
      </div>
    );
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission as any);
  } else if (permissions.length > 0) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions as any)
      : hasAnyPermission(permissions as any);
  } else {
    // No permissions specified, allow access
    hasAccess = true;
  }

  if (!hasAccess) {
    if (showError) {
      return (
        <div className="flex justify-center items-center p-8">
          <div className="text-center">
            <div className="text-red-500 text-lg font-medium mb-2">Geen toegang</div>
            <div className="text-neutral-secondary-mid text-sm">
              Je hebt geen toestemming om deze pagina te bekijken.
            </div>
          </div>
        </div>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Convenience components for specific permissions
export const RequireDashboardAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessDashboard" />
);

export const RequireBuildingsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessBuildings" />
);

export const RequireUnitsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessUnits" />
);

export const RequireContactsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessContacts" />
);

export const RequireAcquisitionsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessAcquisitions" />
);

export const RequireRentalAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessRental" />
);

export const RequireSettingsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessSettings" />
);

export const RequireDocumentsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessDocuments" />
);

export const RequireTasksAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessTasks" />
);

export const RequireNotesAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessNotes" />
);

export const RequireHelpdeskAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessHelpdesk" />
);

export const RequireMaintenanceAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessMaintenance" />
);

export const RequireRenovationAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessRenovation" />
);

export const RequireInsuranceAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessInsurance" />
);

export const RequirePicturesAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessPictures" />
);

export const RequireClustersAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessClusters" />
);

export const RequireConversationsAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessConversations" />
);

export const RequireDueDiligenceAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessDueDiligence" />
);

export const RequireBuildingAttributesAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessBuildingAttributes" />
);

export const RequireGeneralAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessGeneral" />
);

export const RequireCorporateAccess: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} permission="canAccessCorporate" />
);

// Special guard for checking ANY permission (used for companies page)
export const RequireAnyPermission: React.FC<Omit<PermissionGuardProps, 'permission'>> = (props) => (
  <PermissionGuard {...props} requireAll={false} />
);
